// src/pages/oauth/StartLink.jsx
import React, { useEffect, useState } from 'react';
import { API_BASE_URL, FILES_BASE_URL } from '../../utils/media';
import { createPkcePair } from '../../utils/pkce';
import { Link, useNavigate } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';

const AUTH_BASE = FILES_BASE_URL; // same origin as server (no /api)

export default function StartLink() {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [scope, setScope] = useState('openid profile email');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/users/oauth/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || 'Failed to load clients');
        setClients(data.clients || []);
      } catch (e) {
        setError(e.message || 'Failed to load clients');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const chosen = clients.find(c => c.clientId === clientId);
  useEffect(() => {
    if (chosen && chosen.redirectUris?.length) {
      setRedirectUri(chosen.redirectUris[0]);
    } else {
      setRedirectUri('');
    }
  }, [clientId]); // eslint-disable-line

  async function start() {
    try {
      setError('');
      if (!clientId) throw new Error('Choose a client');
      if (!redirectUri) throw new Error('Choose a redirect URI');

      // ask server for sanitized scopes + branding (also enforces approval gate)
      const token = localStorage.getItem('token');
      const url = new URL(`${API_BASE_URL}/users/oauth/links/${encodeURIComponent(clientId)}/start`);
      url.searchParams.set('redirect_uri', redirectUri);
      url.searchParams.set('scope', scope);

      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.message || 'Could not start link');

      // build PKCE
      const { code_verifier, code_challenge } = await createPkcePair();
      sessionStorage.setItem('pkce_verifier', code_verifier);
      sessionStorage.setItem('oauth_client_id', j.clientId);
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);
      sessionStorage.setItem('oauth_scope', j.authorizeParams.scope);

      // the real /oauth/authorize url
      const authUrl = new URL(`${AUTH_BASE}/oauth/authorize`);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('client_id', j.authorizeParams.client_id);
      authUrl.searchParams.set('redirect_uri', j.authorizeParams.redirect_uri);
      authUrl.searchParams.set('scope', j.authorizeParams.scope);
      authUrl.searchParams.set('code_challenge_method', 'S256');
      authUrl.searchParams.set('code_challenge', code_challenge);

      // trampoline: sets session cookie, then redirects back to the authorize URL
      const continueUrl = encodeURIComponent(authUrl.toString());
      window.location.href = `${AUTH_BASE}/oauth/signin?continue=${continueUrl}`;
    } catch (e) {
      setError(e.message || 'Failed to begin OAuth');
    }
  }

  return (
    <PublicLayout>
      <div className="px-4 py-8 sm:px-6 sm:py-14">
        <div className="mx-auto max-w-4xl rounded-[2.5rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.03))] p-6 text-white shadow-[0_30px_90px_rgba(0,0,0,0.3)] backdrop-blur-xl sm:p-8 lg:p-10">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-amber-200">Client panel</p>
              <h1 className="mt-4 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
                Connect an app with Shimbirr OAuth.
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300">
                Choose a client, select a registered redirect, and continue into the secure PKCE flow. This now uses the same responsive shell as the rest of the client-facing experience.
              </p>
            </div>
            <Link
              to="/dashboard"
              className="rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Back to admin
            </Link>
          </div>

          {loading ? <div className="mt-8 text-slate-300">Loading clients…</div> : null}
          {error ? (
            <div className="mt-8 rounded-[1.4rem] border border-red-800 bg-red-900/40 px-4 py-3 text-sm text-red-100">
              {error}
            </div>
          ) : null}

          {!loading ? (
            <div className="mt-8 grid gap-5">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-300">Client</label>
                <select
                  className="w-full rounded-full border border-white/10 bg-[#0b161c] px-5 py-3 text-white outline-none transition focus:border-amber-300/40"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                >
                  <option value="">Select client…</option>
                  {clients.map((c) => (
                    <option key={c.clientId} value={c.clientId}>
                      {c.name || c.clientId}
                    </option>
                  ))}
                </select>
              </div>

              {clientId ? (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-300">Redirect URI</label>
                  <select
                    className="w-full rounded-full border border-white/10 bg-[#0b161c] px-5 py-3 text-white outline-none transition focus:border-amber-300/40"
                    value={redirectUri}
                    onChange={(e) => setRedirectUri(e.target.value)}
                  >
                    <option value="">Select redirect URI…</option>
                    {(chosen?.redirectUris || []).map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                  {!chosen?.redirectUris?.length ? (
                    <p className="mt-2 text-xs text-amber-300">
                      This client has no saved redirect URIs yet.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-300">Scopes</label>
                <input
                  className="w-full rounded-full border border-white/10 bg-[#0b161c] px-5 py-3 text-white outline-none transition focus:border-amber-300/40"
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  placeholder="openid profile email"
                />
                <p className="mt-2 text-xs text-slate-500">
                  The server will sanitize scopes against the client&apos;s allowed scopes.
                </p>
              </div>

              <button
                onClick={start}
                disabled={!clientId || !redirectUri}
                className="w-full rounded-full bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-300 px-4 py-3 text-lg font-semibold text-slate-950 shadow-[0_18px_50px_rgba(245,178,33,0.22)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Continue to Sign In
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </PublicLayout>
  );
}
